import { cn } from "@/lib/utils";
import { UseQueryResult } from "@tanstack/react-query";
import React from "react";
import WhiteLabelLogo from "../_module/loading/white-label-logo";
import ErrorPage from "./ErrorPage";
import { useNavigate } from "@tanstack/react-router";

type BaseProps<TData> = {
	query: UseQueryResult<TData, Error>;
	children: (data: TData) => React.ReactNode;
	customErrorUI?: (error: Error) => React.ReactNode;
	isFullscreenLoading?: boolean;
};

type WithLoadingClassName<TData> = BaseProps<TData> & {
	loadingClassName?: string;
	customLoadingUI?: never;
};

type WithCustomLoadingUI<TData> = BaseProps<TData> & {
	loadingClassName?: never;
	customLoadingUI: React.ReactNode;
};

// Only one of loadingClassName or customLoadingUI can be provided

type Props<TData> = WithLoadingClassName<TData> | WithCustomLoadingUI<TData>;

function PageLoader<TData>(props: Props<TData>) {
	const navigate = useNavigate();

	const {
		query,
		children,
		customLoadingUI,
		customErrorUI,
		loadingClassName,
		isFullscreenLoading = false,
	} = props;

	if (query.status === "pending") {
		// loadingClassName is only applied if customLoadingUI is not used
		return customLoadingUI ? (
			<>{customLoadingUI}</>
		) : (
			<div
				className={cn(
					isFullscreenLoading
						? "w-full h-[calc(100dvh-4rem-5rem)] "
						: "w-full h-full pt-20",
					"flex items-center justify-center",
					"kashuLoadingScreen",
					loadingClassName,
				)}
			>
				<WhiteLabelLogo />
			</div>
		);
	}

	if (query.status === "error") {
		return customErrorUI ? (
			<>{customErrorUI(query.error)}</>
		) : (
			<div
				className={cn("h-full w-full", {
					"w-full h-[calc(100dvh-4rem-5rem)]": isFullscreenLoading,
				})}
			>
				<ErrorPage
					onClose={() => {
						navigate({
							to: "/main/home",
						});
					}}
				/>
			</div>
		);
	}

	return children(query.data as TData);
}

export default PageLoader;
